import { Link } from "react-router-dom";
import SeriesNavigation from "../../components/SeriesNavigation";
import FadeInSection from "../../components/FadeInSection";
import { media } from "../../data/navigationData";
import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";
import { trackEvent } from "../../utils/analytics";

export default function NbnNews() {
  const nbnNewsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "NBN News: NSW Premier visits John Hunter Hospital to celebrate landmark stroke trial results",
    "url": "https://www.thrombio.com.au/media/nbnnews",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Media",
      "description": "NSW Premier visits John Hunter Hospital to celebrate landmark stroke trial results."
    }
  };

  return (
    <>
      <SEO
        title="NBN News: NSW Premier visits John Hunter Hospital to celebrate landmark stroke trial results"
        description="NBN News: NSW Premier visits John Hunter Hospital to celebrate landmark stroke trial results"
        keywords={['NBN News', 'John Hunter Hospital', 'Hunter Medical Research', 'Red Blood Cell Roadblocks', 'TBO-309', 'Stroke Research', 'Prof Shaun Jackson', 'Microcirculation', 'Microclots', 'Red cell aggregates', 'Stroke', 'TBO-309', 'Stroke recovery', 'Blood vessel blockage', 'Anti-clotting drug', 'Stroke treatment', 'Novel therapeutics', 'Anti-platelet', 'Clinical trial', 'Platelet inhibitor', 'Sydney-developed drug']}
        ogImage="/video-poster-9News.png"
        schema={nbnNewsSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="nbnnews" />

      <div className="bg-primary">
        <div className="page-width px-6 md:px-20 pt-[18px] pb-24">
          <nav className="font-sans text-[1.8rem] font-normal leading-[2.3rem]" aria-label="Breadcrumb">
            <Link to="/media" className="text-white hover:text-primary-red transition-colors">
              Media
            </Link>
            <span className="mx-1 text-white"> | </span>
            <span className="text-[#5C95A4]">NBN News: NSW Premier visits JHH to celebrate landmark stroke trial results</span>
          </nav>
        </div>

        <div className="page-width px-6 md:px-20 pb-16 text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 mb-20 lg:items-end">
            <FadeInSection className="lg:flex-shrink-0">
              <div className="flex flex-col items-center lg:items-start lg:justify-end">
                <img src="/nbn-news-logo.png" alt="NBN News Logo" width={491} height={281} className="md:h-[165px] w-auto" />
              </div>
            </FadeInSection>

            <FadeInSection className="delay-500 flex-1 max-w-[800px]">
              <div>
                <div className="w-full bg-gray-200 rounded-lg overflow-hidden relative group">
                  {/* Custom Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity duration-300 z-10">
                    <div className="bg-black/60 rounded-full p-6 backdrop-blur-sm">
                      <svg
                        className="w-16 h-16 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  {/* NBN News Video */}
                  <video
                    controls
                    poster="/nbn-video-poster.webp"
                    width={925}
                    height={519}
                    onPlay={() => trackEvent('video_play', { video_name: 'nbnnews-thrombio', page_path: '/media/nbnnews' })}
                    className="w-full h-auto [&::-webkit-media-controls]:opacity-0 [&::-webkit-media-controls]:transition-opacity [&::-webkit-media-controls]:duration-300 hover:[&::-webkit-media-controls]:opacity-100"
                  >
                    <source src="/nbn-news-video.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </FadeInSection>
          </div>

          <div className="flex flex-col gap-6 mb-[30px]">
            <FadeInSection className="delay-500">
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                NSW Premier Chris Minns and Shaun Jackson, scientific founder of ThromBio, have marked a major milestone in stroke treatment during a visit to John Hunter Hospital, meeting clinicians, researchers, and trial patient Alan Johnson to highlight the promise of breakthrough anti-clotting drug TBO-309.
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                Alan Johnson, 76, was among nearly 30 patients treated in the STARS trial after suffering a severe ischemic stroke in February. His rapid recovery—discharge from intensive care and hospital within four days, and a full return of speech and movement—has become a defining example of the therapy’s potential.
              </div>
            </FadeInSection>

            {/* Alan's Newcastle Herald Quote */}
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] text-center my-4">
                <span className="italic">"Honestly, I thought I was going to die."< br />
                  Alan suffered a severe stroke on February 13 that left him unable to speak or move.< br />
                  "The recovery was nothing short of miraculous."< br /></span>
                <span className="font-medium text-[#005aab]">Newcastle Herald< br /></span>
                <span className="font-normal text-[2rem] text-[#005aab]">Read the Newcastle Herald article  <Link to="/media/newcastleherald"><span className="underline">here</span></Link></span>
              </div>
            </FadeInSection>

            <FadeInSection>
              {/* Shaun with STARS patient Alan Image */}
              <div className="mt-5 mt-10 mb-10 max-w-[800px] justify-center self-center">
                <div className="mb-8 width-[400px]">
                  <img
                    src="/professor-shaun-jackson-with-trial-patient-alan-johnson-newcastle.webp"
                    alt="Thrombio Founder Professor Shaun Jackson meets clinical trial patient Alan Johnson at Newcastle Hospital. Photographed by Peter Lorimer, Newcastle Herald."
                    width={1428}
                    height={987}
                    className="w-full h-auto"
                  />
                </div>
                <h2 className="text-[#E0FFFF] leading-[2rem] mb-1">
                  <span className="text-[1.9rem] italic">Clinical trial patient Alan Johnson, meets Thrombio Founder and Thrombosis Research Group Leader Professor Shaun Jackson, at Newcastle Hospital, following 3 decades of medical research and drug development.< br /></span>
                  <span className="text-[#FFFFFF] text-[1.7rem]">Image by Peter Lorimer, Newcastle Herald.</span>
                </h2>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                Clinicians involved in the trial say results have been consistently remarkable. Principal investigator for the STARS trial at the John Hunter Hospital, Professor Chris Levi described witnessing patients regain speech and mobility at unprecedented speeds as “incredibly powerful,” noting that outcomes like Alan’s are not isolated. A clear pattern of accelerated recovery is now emerging, reshaping expectations in acute stroke care.
              </div>
            </FadeInSection>

            {/* Shaun with Chris Minns Image */}
            <div className="mt-5 mt-10 mb-10 max-w-[800px] justify-center self-center">
              <FadeInSection>
                <div className="mb-8 width-[400px]">
                  <img
                    src="/thrombio-founder-shaun-jackson-with-nsw-premier-chris-minns-newcastle.webp"
                    alt="Thrombio Founder Professor Shaun Jackson speaks with NSW Premier Chris Minns at Newcastle Hospital. Photographed by Peter Lorimer, Newcastle Herald."
                    width={1428}
                    height={987}
                    className="w-full h-auto"
                  />
                </div>
              </FadeInSection>
              <FadeInSection>
                <h2 className="text-[#E0FFFF] leading-[2rem] mb-1">
                  <span className="text-[1.9rem] italic">NSW Premier Chris Minns speaks with Thrombio Founder Professor Shaun Jackson, at Newcastle Hospital.< br /></span>
                  <span className="text-[#FFFFFF] text-[1.7rem]">Image by Peter Lorimer, Newcastle Herald.</span>
                </h2>
              </FadeInSection>
            </div>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                With around 46,000 Australians experiencing stroke each year, researchers are working to expand the trial across more hospitals internationally, alongside exploring paramedic-led delivery models to enable faster treatment in emergency settings.
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                The visit highlights growing momentum behind TBO-309 as a potential game-changer in stroke recovery, offering new hope for patients and redefining what is possible in life-saving care.
              </div>
            </FadeInSection>

            {/* Scientists with patient Alan Image */}
            <div className="mt-5 mt-10 mb-10 max-w-[800px] justify-center self-center">
              <FadeInSection>

                <div className="mb-8 width-[400px]">
                  <img
                    src="/thrombio-clinical-trial-staff-meet-trial-patient-alan-johnson.webp"
                    alt="Thrombio Founder Professor Shaun Jackson speaks with NSW Premier Chris Minns at Newcastle Hospital. Photographed by Peter Lorimer, Newcastle Herald."
                    width={1428}
                    height={987}
                    className="w-full h-auto"
                  />
                </div>
              </FadeInSection>
              <FadeInSection>
                <h2 className="text-[#E0FFFF] leading-[2.2rem] mb-1">
                  <span className="text-[1.9rem] italic pb-2">ThromBio scientists with John Hunter Hospital STARS Trial staff with the NSW Premier Chris Minns.< br /></span>
                  <span className="text-[1.7rem]">(from left to right) </span><span className="text-[1.9rem]">Simone Schoenwaelder <span className="text-[1.5rem]">(Researcher, Thrombosis Research Group),</span> Peter Bush <span className="text-[1.5rem]">(Thrombio CEO),</span> NSW Premier Chris Minns, Shaun Jackson <span className="text-[1.5rem]">(Head of Thrombosis Research Group, Thrombio Founder and Director),</span> Chris Levi <span className="text-[1.5rem]">(Conjoint Professor, Stroke Physician),</span> Carlos Garcia-Esperon <span className="text-[1.5rem]">(Conjoint Associate Professor (University of Newcastle) and Senior Staff Neurologist at John Hunter Hospital and the Director of Acute Stroke Service)</span>, Newcastle MP Tim Crakanthorp, Mark Parsons <span className="text-[1.5rem]">(Professor of Neurology UNSW and Liverpool Hospital)</span>, Imala Alwis <span className="text-[1.5rem]">(Researcher, Thrombosis Research Group)</span>, Ben Hofma <span className="text-[1.5rem]">(Researcher, Thrombosis Research Group)</span>, Tracey McCosker <span className="text-[1.5rem]">(CEO, Hunter New England Local Health District)</span>, Alan Johnson <span className="text-[1.5rem]">(Patient and Clinical trials recipient)</span></span>< br />
                  <span className="text-[#FFFFFF] text-[1.7rem]">Image by Peter Lorimer, Newcastle Herald.</span>
                </h2>
              </FadeInSection>
            </div>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] mt-6">
                <Link to="/media/newcastleherald">
                  Read more about Alan's story and the landmark stroke trial, <span className="underline hover:text-[#C3EEFD]">here</span>
                </Link>
              </div>
            </FadeInSection>
          </div>

        </div>
        <SeriesNavigation
          items={media}
          currentSlug="nbnnews"
          basePath="/media"
        />
      </div >
    </>
  );
}